/**
 * Sensor Configuration Validation
 * Validates sensor-specific fields for both Lite (multiple sensors) and Mini (single sensor)
 */
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { SensorType, SENSOR_TYPE_MAP } from '@packages/types/device/lite/sensors';
import {
  SENSOR_CONSTRAINTS as LITE_SENSOR_CONSTRAINTS,
  getSensorTypeInfo,
} from '@packages/types/device/lite/lite_constraints';
import {
  MiniSensorType,
  MINI_SENSOR_TYPE_MAP,
} from '@packages/types/device/mini/sensors';
import {
  SENSOR_CONSTRAINTS as MINI_SENSOR_CONSTRAINTS,
  getMiniSensorInfo,
} from '@packages/types/device/mini/mini_constraints';
import type { FieldError } from '../../types/validation';
import { convertConstraintToCurrentUnit } from '../../utils/validators';
import { useValidationHelpers } from './helpers';
import { DeviceType } from '@packages/types/device/common/deviceTypes';
import { i18nKeys } from '@/i18n/i18nKeyGen';

/** constraint 값을 어떤 소스에서 가져올지 결정 */
type ConstraintSource = 'transformed' | 'transformedOrRaw' | 'raw';

interface FieldSpec {
  field: string;
  hintKey: string;
  source: ConstraintSource;
  /** false면 해당 필드 검증을 건너뜀 (조건부 필드). 기본값은 검증함 */
  enabled?: boolean;
}

interface ResolvedConstraint {
  constraint: Record<string, any>;
  hasTwaStel: boolean;
  enableBumpThreshold: boolean;
  /** O2 또는 LEL 계열 센서인지 (high_alarm > low_alarm 교차 검증 대상, LITE 전용) */
  requiresAlarmOrderCheck: boolean;
}

/** high_alarm > low_alarm 교차 검증이 필요한 LITE 센서 타입 (O2 & LEL 공통) */
const ALARM_ORDER_CHECK_SENSOR_TYPES: SensorType[] = [
  SensorType.O2,
  SensorType.O2LF,
  SensorType.LEL_LPC,
  SensorType.LEL_1Series,
  SensorType.LEL_IR,
];

/**
 * 센서 타입에 해당하는 constraint와 지원 플래그를 해석한다.
 * LITE/MINI 간 차이(타입 맵, constraint 테이블, CO2_CRANBERRY 단위 처리)는 이 함수에 격리한다.
 */
function resolveSensorConstraint(
  deviceType: DeviceType,
  sensorData: Record<string, unknown>
): ResolvedConstraint | null {
  const rawSensorType = (sensorData.type as any)?.value;

  if (deviceType === DeviceType.LITE) {
    let validSensorType = SensorType.CO;
    if (typeof rawSensorType === 'string') {
      validSensorType = SENSOR_TYPE_MAP[rawSensorType] || SensorType.CO;
    } else if (typeof rawSensorType === 'number') {
      validSensorType = rawSensorType as SensorType;
    }

    let constraint = LITE_SENSOR_CONSTRAINTS[validSensorType];

    // CO2_CRANBERRY: 단위(VOL)에 따라 constraint 테이블 교체
    if (validSensorType === SensorType.CO2_CRANBERRY) {
      const currentUnit = (sensorData as any).gas_conc_unit?.value;
      if (currentUnit === 1) {
        constraint = LITE_SENSOR_CONSTRAINTS[SensorType.CO2_CRANBERRY_VOL];
      }
    }

    if (!constraint) return null;

    const sensorInfo = getSensorTypeInfo(validSensorType);
    return {
      constraint: constraint as Record<string, any>,
      hasTwaStel: !!sensorInfo?.has_twa_stel,
      enableBumpThreshold: !!sensorInfo?.enable_bump_test_threshold,
      requiresAlarmOrderCheck: ALARM_ORDER_CHECK_SENSOR_TYPES.includes(validSensorType),
    };
  }

  let validSensorType = MiniSensorType.CO;
  if (typeof rawSensorType === 'string') {
    validSensorType = MINI_SENSOR_TYPE_MAP[rawSensorType] || MiniSensorType.CO;
  } else if (typeof rawSensorType === 'number') {
    validSensorType = rawSensorType as MiniSensorType;
  }

  const constraint = MINI_SENSOR_CONSTRAINTS[validSensorType];
  if (!constraint) return null;

  const sensorInfo = getMiniSensorInfo(validSensorType);
  return {
    constraint: constraint as Record<string, any>,
    hasTwaStel: !!sensorInfo?.has_twa_stel,
    // bump_test_threshold는 MINI에서 검증하지 않음
    enableBumpThreshold: false,
    // high_alarm > low_alarm 교차 검증은 BELLLITE 전용 요청 사항
    requiresAlarmOrderCheck: false,
  };
}

/**
 * Custom hook for sensor configuration validation
 */
export const useSensorValidation = (deviceType: DeviceType) => {
  const { t } = useTranslation('config');
  const { validateAndHint } = useValidationHelpers();

  /**
   * Validate sensor configuration
   * For Lite: validates one sensor from the array
   * For Mini: validates the single sensor
   */
  const validateSensorConfig = useCallback(
    (
      sensorData: Record<string, unknown>
    ): { errors: Record<string, FieldError>; hints: Record<string, string> } => {
      const errors: Record<string, FieldError> = {};
      const hints: Record<string, string> = {};

      // 0. 비활성화된 센서는 값 수정이 불필요하므로 검증 자체를 건너뜀 (LITE 전용 필드)
      if (sensorData.sensor_disabled === true) {
        return { errors, hints };
      }

      // 1. 센서 타입에 맞는 constraint와 sensorInfo를 해석 (LITE/MINI 분기는 여기서만)
      const resolved = resolveSensorConstraint(deviceType, sensorData);
      if (!resolved) return { errors, hints };

      const { constraint, hasTwaStel, enableBumpThreshold } = resolved;
      const transformedConstraint = convertConstraintToCurrentUnit(constraint);

      // constraint 소스 선택: scale 변환본 / 변환본 우선 후 원본 fallback / 원본 직접
      const pick = (field: string, source: ConstraintSource) => {
        switch (source) {
          case 'raw':
            return constraint[field];
          case 'transformedOrRaw':
            return transformedConstraint[field] || constraint[field];
          default:
            return transformedConstraint[field];
        }
      };

      // custom_lel: lel_CF_Index가 Custom일 때만 검증 (LITE 전용 필드)
      const cfIndexValue = sensorData.lel_CF_Index;
      const isCustom =
        deviceType === DeviceType.LITE &&
        !!cfIndexValue &&
        typeof cfIndexValue === 'object' &&
        'label' in cfIndexValue &&
        (cfIndexValue as { label: string }).label === 'Custom';

      // 2. 필드 스펙을 순회하며 검증 (조건부 필드는 enabled 플래그로 제어)
      const fieldSpecs: FieldSpec[] = [
        { field: 'calibration_gas_conc', hintKey: 'hint_calibrationGasConc', source: 'transformed' },
        { field: 'calibration_interval', hintKey: 'hint_calibrationInterval', source: 'transformed' },
        { field: 'bump_interval', hintKey: 'hint_bumpInterval', source: 'transformed' },
        {
          field: 'bump_test_threshold',
          hintKey: 'hint_bumpTestThreshold',
          source: 'transformedOrRaw',
          enabled: enableBumpThreshold,
        },
        { field: 'low_alarm', hintKey: 'hint_lowAlarm', source: 'transformedOrRaw' },
        { field: 'high_alarm', hintKey: 'hint_highAlarm', source: 'transformedOrRaw' },
        {
          field: 'twa_alarm',
          hintKey: 'hint_twaAlarm',
          source: 'transformedOrRaw',
          enabled: hasTwaStel,
        },
        {
          field: 'stel_alarm',
          hintKey: 'hint_stelAlarm',
          source: 'transformedOrRaw',
          enabled: hasTwaStel,
        },
        // twa_period/stel_period는 scale이 없으므로 원본 constraint를 직접 사용
        { field: 'twa_period', hintKey: 'hint_twaPeriod', source: 'raw', enabled: hasTwaStel },
        { field: 'stel_period', hintKey: 'hint_stelPeriod', source: 'raw', enabled: hasTwaStel },
        { field: 'custom_lel', hintKey: 'hint_customLel', source: 'transformed', enabled: isCustom },
      ];

      fieldSpecs.forEach(({ field, hintKey, source, enabled }) => {
        if (enabled === false) return;
        validateAndHint(field, sensorData[field], pick(field, source), errors, hints, hintKey);
      });

      // 3. O2 & LEL 공통: high_alarm이 low_alarm보다 큰 값인지 교차 검증 (각 필드 range 검증을 통과한 경우에만)
      if (
        resolved.requiresAlarmOrderCheck &&
        !errors.low_alarm &&
        !errors.high_alarm &&
        typeof sensorData.low_alarm === 'number' &&
        typeof sensorData.high_alarm === 'number' &&
        sensorData.high_alarm <= sensorData.low_alarm
      ) {
        const message = t(i18nKeys.config.error_highAlarmMustExceedLowAlarm);
        errors.low_alarm = { hasError: true, message };
        errors.high_alarm = { hasError: true, message };
      }

      return { errors, hints };
    },
    [deviceType, validateAndHint, t]
  );

  return {
    validateSensorConfig,
  };
};
